import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, X, Printer, Download } from 'lucide-react';
import { cn } from "@/lib/utils";
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import { userService } from "../../_services";
import { generateMemberId } from "../../utils/cardHelpers";
import CardFront from "../Summary/CardFront";
import CardBack from "../Summary/CardBack";
import AppleWallet from "../../images/apple-wallet.svg";
import GoogleWallet from "../../images/google-wallet.svg";
import AppleModal from "./AppleModal";
import OrderPage from "../Admin/Users/OrderPage";
import Resources from "../../resources.json";
import shareCardBg from "../../images/share-card-bg.png";
import appWalletBg from "../../images/app-wallet-bg.png";
import orderCardBg from "../../images/order-card-bg.png";

/**
 * ShareCard Page - Complete dev-v3 layout with Shadcn
 * 4 sections: Download Card, Add to Wallet, Order Cards, Share Card
 */
function ShareCard() {
    // User & Group State
    const [groupNumbers, setGroupNumbers] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupData, setGroupData] = useState(null);
    const [shortname, setShortname] = useState('');
    const [fullName, setFullName] = useState('');
    
    // Device Detection
    const [deviceName, setDeviceName] = useState('');
    const [browserName, setBrowserName] = useState('');
    
    // Wallet State
    const [applePopup, setApplePopup] = useState(false);
    const [showApplePopup, setShowApplePopup] = useState(false);
    
    // Email Sharing State
    const [emailList, setEmailList] = useState([]);
    const [emailField, setEmailField] = useState('');
    const [errors, setErrors] = useState('');
    const [errorEmail, setErrorEmail] = useState('');
    const [loader, setLoader] = useState(false);
    
    // Order Popup State
    const [showOrderPopup, setShowOrderPopup] = useState(false);
    
    // Loading State
    const [loading, setLoading] = useState(true);

    // Generate member ID based on user role
    const memberId = generateMemberId('glic_card');

    useEffect(() => {
        try {
            const userAgent = window.navigator.userAgent;
            const devicetype = getUserAgent(userAgent);
            const browsername = getBrowserName(userAgent);
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            const usernamedata = window.atob(localStorage.getItem('hacker_user_detail'));

            setShortname(usernamedata.split(':')[0]);
            setFullName(usernamedata.split(':')[1]);
            setDeviceName(devicetype);
            setBrowserName(browsername);

            getConsumerGroups(userId, userName);

            const isSafari = /Safari/.test(userAgent) && /Version/.test(userAgent) && !/CriOS/.test(userAgent);
            if (devicetype === 'iPhone' && !isSafari) {
                setShowApplePopup(true);
            }
        } catch (error) {
            console.error('Error initializing:', error);
        }
    }, []);

    const getUserAgent = (userAgent) => {
        if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iPhone';
        if (/Android/i.test(userAgent)) return 'Android';
        return 'Desktop';
    };

    const getBrowserName = (userAgent) => {
        if (/Firefox/i.test(userAgent)) return 'Firefox';
        if (/Chrome/i.test(userAgent)) return 'Chrome';
        if (/Safari/i.test(userAgent)) return 'Safari';
        if (/MSIE|Trident/i.test(userAgent)) return 'Internet Explorer';
        if (/Edge/i.test(userAgent)) return 'Edge';
        if (/Opera|OPR/i.test(userAgent)) return 'Opera';
        if (/Brave/i.test(userAgent)) return 'Brave';
        if (/Vivaldi/i.test(userAgent)) return 'Vivaldi';
        if (/Yandex/i.test(userAgent)) return 'Yandex';
        if (/Chromium/i.test(userAgent)) return 'Chromium';
        return 'Unknown';
    };

    const getConsumerGroups = async (userId, userName) => {
        try {
            const apidata = await userService.GetConsumerGroups(userId, userName);

            if (apidata && apidata.length > 0) {
                setGroupNumbers(apidata);
                setSelectedGroup(apidata[0].groupnumber);
                setGroupData(apidata[0]);
                setLoading(false);

                // Generate passes for first group
                appleGooglePassClick('apple', apidata[0].groupnumber);
                appleGooglePassClick('google', apidata[0].groupnumber);
            }
        } catch (error) {
            console.error('Error getting consumer groups:', error);
            setLoading(false);
        }
    };

    const updateGroup = (e) => {
        const groupNo = e.target.value;
        const sgd = groupNumbers.find(item => item.groupnumber === groupNo);
        if (sgd) {
            setSelectedGroup(groupNo);
            setGroupData(sgd);
            appleGooglePassClick('apple', groupNo);
            appleGooglePassClick('google', groupNo);
        }
    };

    const printCard = () => {
        const cardElement = document.getElementById(cardId);
        if (!cardElement) {
            window.print();
            return;
        }

        const styleId = '__card-print-style__';
        document.getElementById(styleId)?.remove();

        // Strategy: keep the card in its original React DOM position so the
        // canvas (drawn by CanvasCardFront's useEffect) renders correctly.
        //
        // Blank-page problem:
        //   visibility:hidden keeps hidden elements in layout flow ? body stays
        //   tall ? browser generates extra blank pages.
        //
        // Fix � two parts:
        //   1. Collapse the body to zero height with overflow:hidden so the
        //      document flow produces no pages beyond what is needed.
        //   2. Use position:fixed (not absolute) for the card.
        //      Fixed elements are positioned relative to the page box, NOT the
        //      body's flow, so they are never clipped by body overflow:hidden.
        //      The card renders on page 1 regardless of body height.
        const styleEl = document.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            @media print {
                html, body {
                    height: 0 !important;
                    overflow: hidden !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                body * { visibility: hidden !important; }
                #${cardId}, #${cardId} * { visibility: visible !important; }
                #${cardId} {
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    max-width: 700px !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                /* Remove the global 60px bottom margin so both cards fit on
                   one page without overflowing onto a blank second page */
                #${cardId} .order_card {
                    margin-bottom: 0 !important;
                }
            }
        `;
        document.head.appendChild(styleEl);

        const cleanup = () => { document.getElementById(styleId)?.remove(); };

        const fallbackTimer = setTimeout(cleanup, 60000);
        window.addEventListener('afterprint', function onAfterPrint() {
            clearTimeout(fallbackTimer);
            window.removeEventListener('afterprint', onAfterPrint);
            cleanup();
        });

        window.print();
    };

    const downloadCard = (cardId, name) => {
        const el = document.getElementById(cardId);
        if (!el) return;

        domtoimage.toPng(el)
            .then(function (dataUrl) {
                const doc = new jsPDF("p", "mm", "a4");
                const pdfWidth = doc.internal.pageSize.getWidth();
                const pdfHeight = doc.internal.pageSize.getHeight();
                const img = new Image();
                img.src = dataUrl;
                img.onload = function () {
                    // Cap at 480px equivalent in mm (480 � 25.4 / 96 � 127mm)
                    const maxWidth = Math.min(pdfWidth - 20, 127);
                    let fixedWidth = maxWidth;
                    let fixedHeight = (img.height / img.width) * fixedWidth;

                    if (fixedHeight > pdfHeight - 20) {
                        const scaleFactor = (pdfHeight - 20) / fixedHeight;
                        fixedWidth *= scaleFactor;
                        fixedHeight *= scaleFactor;
                    }

                    // Center horizontally on the page
                    const xOffset = (pdfWidth - fixedWidth) / 2;

                    doc.addImage(dataUrl, 'PNG', xOffset, 10, fixedWidth, fixedHeight);

                    // Use Blob URL + anchor click instead of doc.save() which
                    // calls window.open() internally � blocked as a popup on
                    // mobile Safari and Android browsers.
                    const blob = doc.output('blob');
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = name + '.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 200);
                };
            })
            .catch(function (err) {
                console.error('Download failed:', err);
            });
    };

    const appleGooglePassClick = async (passtype, groupnum) => {
        try {
            const bin = Resources.ApplicationResources.cards[0].bin;
            const pcn = Resources.ApplicationResources.cards[0].pcn;
            const memberid = Resources.ApplicationResources.cards[0].memberid;
            const appname = Resources.ApplicationResources.pass[0].appname;
            // URL differentiates apple vs google; API passtype field must be 'generic' (matches dev-v3)
            const requestURL = passtype === 'apple'
                ? Resources.ApplicationResources.pass[0].applelink
                : Resources.ApplicationResources.pass[0].googlelink;

            const apidata = await userService.apple_google_wallet_pass_link(
                requestURL,
                bin,
                pcn,
                groupnum || selectedGroup,
                memberid,
                'generic',   // dev-v3 always sends 'generic' as passtype in the request body
                appname
            );

            if (apidata && apidata !== 'null') {
                if (passtype === 'apple') {
                    window.localStorage.setItem('applePass', apidata);
                } else {
                    window.localStorage.setItem('googlePass', apidata);
                }
            }
        } catch (error) {
            console.error('Error generating pass:', error);
        }
    };

    const appleWalletPopup = () => {
        setApplePopup(!applePopup);
    };

    const openAppleLink = () => {
        const link = window.localStorage.getItem("applePass");
        if (link && link !== 'null') window.open(link, '_blank');
    };

    const openGoogleLink = () => {
        const link = window.localStorage.getItem("googlePass");
        if (link && link !== 'null') window.open(link, '_blank');
    };

    // Email Sharing Functions (from dev-v3)
    const handleEmailKeyUp = (e) => {
        try {
            setErrors('');
            setErrorEmail('');
            const email = e.target.value;

            if (email.indexOf(',') > -1) {
                e.target.value = '';
                const inputemail = email.replace(',', '').toLowerCase();

                if (userService.ValidEmailRegex(inputemail) === true) {
                    let existingitem = [...emailList];

                    const isDuplicate = existingitem.some(item => item === inputemail);

                    if (isDuplicate === false) {
                        if (existingitem.length < 10) {
                            existingitem.push(inputemail);
                            setEmailList(existingitem);
                            setEmailField('');
                        } else {
                            setErrorEmail('Max allowed email id is up to 10');
                            setEmailField('');
                            setLoader(false);
                        }
                    } else {
                        setErrorEmail('Email already exits');
                        setEmailField('');
                        setLoader(false);
                    }
                } else {
                    setErrorEmail('Invalid email');
                    setEmailField('');
                    setLoader(false);
                }
            }
        } catch (error) {
            console.error('Error in handleEmailKeyUp:', error);
        }
    };

    const removeEmail = (index) => {
        try {
            setErrors('');
            const updatedList = [...emailList];
            updatedList.splice(index, 1);
            setEmailList(updatedList);
        } catch (error) {
            console.error('Error removing email:', error);
        }
    };

    const shareCard = async () => {
        setLoader(true);
        setErrorEmail('');

        try {
            let emails = [...emailList];
            const fValue = emailField.trim();
            let isCheck = true;

            if (errorEmail.length < 1) {
                if (fValue === '' && emails.length < 1) {
                    setErrorEmail('Email required');
                    setLoader(false);
                    isCheck = false;
                } else if (fValue !== '') {
                    if (userService.ValidEmailRegex(fValue) === false) {
                        setEmailField('');
                        setErrorEmail('Invalid email');
                        setLoader(false);
                        isCheck = false;
                    } else {
                        if (!emails.includes(fValue.toLowerCase())) {
                            emails.push(fValue);
                        }
                    }
                }

                if (emails.length <= 10) {
                    if (emails.length > 0 && isCheck) {
                        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                        const userId = Usermeta_Data.split(':')[0];
                        const userName = Usermeta_Data.split(':')[1];
                        const JsonEmails = JSON.stringify(emails);

                        try {
                            const apidata = await userService.Share_Card(userId, userName, JsonEmails);

                            if (apidata[0].code === "1") {
                                setErrors('Your card is successfully shared');
                                setEmailField('');
                                setEmailList([]);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            } else {
                                setErrors(apidata[0].message);
                                setLoader(false);
                                setTimeout(() => {
                                    setErrors('');
                                }, 2000);
                            }
                        } catch (error) {
                            console.error('Error sharing card:', error);
                            setLoader(false);
                        }
                    }
                } else {
                    setEmailField('');
                    setErrorEmail('Max allowed email id is up to 10');
                    isCheck = false;
                    setLoader(false);
                }
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error('Error in shareCard:', error);
            setLoader(false);
        }
    };

    const handleEmailFocus = () => {
        setErrors('');
        setErrorEmail('');
    };

    const handleOrderClick = () => {
        setShowOrderPopup(true);
    };

    const toggleOrderPopup = () => {
        setShowOrderPopup(false);
    };

    // Check authentication
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    localStorage.setItem('pagename', 'consumerordercard');

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    const cardId = `card-${selectedGroup}`;
    const cardsData = Resources.ApplicationResources.cards || [];
    const showAppleWallet = deviceName !== 'Android';
    const showGoogleWallet = deviceName !== 'iPhone';

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground">Share Card</h1>
                <p className="text-muted-foreground mt-2">
                    Download, print, or share your prescription savings card
                </p>
            </div>

            {/* 1. Download Card Section */}
            <Card>
                <CardContent className="p-4 sm:p-6 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-xl font-semibold min-w-0">Download Your NuLifeSpan Rx Card</h3>
                        
                        {/* Print/Download Buttons */}
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="outline"
                                onClick={printCard}
                                size="sm"
                            >
                                <Printer className="h-4 w-4 mr-2" />
                                Print
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => downloadCard(cardId, `${cardsData[0]?.pcn || 'Card'}_${selectedGroup}`)}
                                size="sm"
                            >
                                <Download className="h-4 w-4 mr-2" />
                                Download
                            </Button>
                        </div>
                    </div>

                    {/* Group Selector */}
                    {groupNumbers.length > 1 && (
                        <div>
                            <Label className="block text-sm font-medium mb-2">Select Group Number</Label>
                            <Select value={selectedGroup} onValueChange={(val) => updateGroup({ target: { value: val } })}>
                                <SelectTrigger className="w-full max-w-xs">
                                    <SelectValue placeholder="Select group..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {groupNumbers.map((group, index) => (
                                        <SelectItem key={index} value={group.groupnumber}>
                                            {group.groupnumber}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    {/* Card Display - Both Front and Back in Same Container */}
                    {cardsData.map((card, index) => (
                        <div key={index} id={cardId} className="cards space-y-4 print-only w-full max-w-[480px] overflow-x-hidden">
                            <CardFront
                                PrimaryGn={selectedGroup}
                                pcn={card.pcn}
                                bin={card.bin}
                                mId={memberId}
                                beneficiaryName={groupData?.beneficiaryName || ''}
                            />
                            <CardBack
                                cardData={card}
                                groupNumber={selectedGroup}
                                mId={memberId}
                            />
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Grid for Sections 2, 3, 4 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 2. Add to Wallet Section */}
                <Card>
                    <CardContent 
                        className="p-4 sm:p-6 rounded-lg h-[250px] flex flex-col justify-start"
                        style={{
                            background: `#fff url(${appWalletBg}) right bottom no-repeat`,
                            backgroundSize: 'auto'
                        }}
                    >
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Add to wallet</h3>
                            <p className="text-muted-foreground mb-4">
                                It's apple wallet or Google wallet which you can use on the go
                            </p>
                        </div>
                        <div className="flex gap-3">
                            {showAppleWallet && (
                                <button
                                    onClick={showApplePopup ? appleWalletPopup : openAppleLink}
                                    className="transition-transform hover:scale-105"
                                >
                                    <img src={AppleWallet} alt="Apple Wallet" className="h-10" />
                                </button>
                            )}
                            {showGoogleWallet && (
                                <button
                                    onClick={openGoogleLink}
                                    className="transition-transform hover:scale-105"
                                >
                                    <img src={GoogleWallet} alt="Google Wallet" className="h-10" />
                                </button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* 3. Order Cards Section */}
                <Card>
                    <CardContent 
                        className="p-4 sm:p-6 rounded-lg h-[250px] flex flex-col justify-start"
                        style={{
                            background: `#fff url(${orderCardBg}) right bottom no-repeat`,
                            backgroundSize: 'auto'
                        }}
                    >
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Order cards</h3>
                            <p className="text-muted-foreground mb-4">
                                Buy physical printed cards which you can hand out to friends and family
                            </p>
                        </div>
                        <Button variant="secondary" onClick={handleOrderClick} className="w-auto max-w-[120px]">
                            Order now
                        </Button>
                    </CardContent>
                </Card>

                {/* 4. Share Card Section (Full Width) */}
                <Card className="lg:col-span-2">
                    <CardContent 
                        className="p-4 sm:p-6 rounded-lg"
                        style={{
                            background: `#fff url(${shareCardBg}) right bottom no-repeat`,
                            backgroundSize: 'auto'
                        }}
                    >
                        <h3 className="text-lg font-semibold mb-2">Share card</h3>
                        <p className="text-base text-muted-foreground mb-4">
                            Enter up to 10 Email ID's separated by a comma.
                        </p>

                        <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row gap-2 max-w-2xl">
                                <div className="flex-1 relative">
                                    <Input
                                        id="shareEmail"
                                        type="text"
                                        value={emailField}
                                        onChange={(e) => setEmailField(e.target.value)}
                                        onKeyUp={handleEmailKeyUp}
                                        onFocus={handleEmailFocus}
                                        className={cn("peer h-10 mb-0", errorEmail && "border-destructive")}
                                        placeholder=" "
                                    />
                                    <Label 
                                        htmlFor="shareEmail" 
                                        className="absolute left-3 -top-2.5 bg-background px-1 text-sm text-foreground transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-muted-foreground peer-focus:-top-2.5 peer-focus:text-sm peer-focus:text-foreground pointer-events-none"
                                    >
                                        Enter your e-mail address
                                    </Label>
                                    {errorEmail && (
                                        <p className="text-xs text-destructive mt-1">{errorEmail}</p>
                                    )}
                                </div>

                                <Button
                                    onClick={shareCard}
                                    disabled={loader}
                                    variant="secondary"
                                    className="h-10 px-8 w-full sm:w-auto"
                                >
                                    {loader ? (
                                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sharing...</>
                                    ) : (
                                        'Share'
                                    )}
                                </Button>
                            </div>

                            {emailList.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {emailList.map((email, index) => (
                                        <Badge key={index} variant="secondary" className="gap-1 pr-1">
                                            {email}
                                            <button
                                                onClick={() => removeEmail(index)}
                                                className="ml-1 hover:bg-black/20 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            )}

                            {errors && (
                                <Alert variant={errors.includes('successfully') ? 'success' : 'destructive'}>
                                    <AlertDescription>{errors}</AlertDescription>
                                </Alert>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Modals */}
            {applePopup && (
                <AppleModal closeModal={appleWalletPopup} />
            )}

            {/* Order Page Modal */}
            {showOrderPopup && (
                <Dialog open={showOrderPopup} onOpenChange={toggleOrderPopup}>
                    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto p-0 [&>button:last-child]:hidden">
                        <OrderPage 
                            toggle_user_detail={toggleOrderPopup} 
                            forCard={true} 
                            groupNumbers={groupNumbers} 
                        />
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

export default ShareCard;

